import { useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Col, Row, Form, Modal, Tooltip, OverlayTrigger, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";

import { actionTypeSelector, importEmployeesSelector, personNumberSelector } from "../reducers";
import { getLocaleText } from "../actions/datagrid";
import { defaultColDef, contextMenu, sideBar, statusBar } from "./grid";
import { importEmployees } from "../actions/persons/importemployees";
import { getPersonNumber } from "../actions/persons/personnumberget";
import Loading from "./loaders/loading";
import { CheckCircleIcon, CrossCircleIcon } from "./icons";
import { Button } from "./Button.tsx";
import Switch from "./Switch";

export function ImportEmployeesModal({ hideModal }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const importEmployeesPost = useCallback((data) => dispatch(importEmployees(data)));
  const personNumberGet = useCallback(() => dispatch(getPersonNumber()));
  const actionType = useSelector(actionTypeSelector);
  const personNumber = useSelector(personNumberSelector);
  const importEmployeesResult = useSelector(importEmployeesSelector);

  const [data, setData] = useState(null);
  const [showLoading, setShowLoading] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [showResultLoading, setShowResultLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    personNumberGet();
  }, []);

  useEffect(() => {
    if (actionType === "GET_PERSON_NUMBER_SUCCEEDED") {
      const newData = { ...data };
      newData["firstNr"] = 1;
      newData["lastNr"] = personNumber;
      setData(newData);
      setShowLoading(false);
    }
    if (actionType === "GET_PERSON_NUMBER_FAIL") {
      setShowLoading(false);
    }
    if (actionType === "IMPORT_EMPLOYEES_SUCCEEDED") {
      setShowResultLoading(false);
      setTableData(importEmployeesResult.employees);
    }
    if (actionType === "IMPORT_EMPLOYEES_FAIL") {
      setShowResultLoading(false);
    }
  }, [actionType]);

  const dataChange = ({ target }) => {
    const newData = { ...data };
    let value = target.type === "checkbox" ? target.checked : target.value;

    if (target.type === "number") {
      if (value < 1) {
        value = 1;
      }
    }

    newData[target.id] = value;
    setData(newData);
  };

  const handleImport = () => {
    setTableData([]);
    dispatch({
      type: "IMPORT_EMPLOYEES_RESET",
      payload: { importEmployees: null },
    });
    importEmployeesPost(data);
    setShowResultLoading(true);
    setShowResults(true);
  };

  const gridRef = useRef();

  const importStatusValueFormatter = (params) => {
    if (params.value === true || params.value === "true") {
      return t("ImportStatus_SUCCESS", "Onnistui");
    } else if (params.value === false || params.value === "false") {
      return t("ImportStatus_FAIL", "Epäonnistui");
    }
  };

  const importActionValueFormatter = (params) => {
    return t("ImportAction_" + params.value, params.value);
  };

  const importErrorValueFormatter = (params) => {
    if (params && params.data && params.data.errorMessages) {
      return params.data.errorMessages.map(handleErrorMessages);
    }
  };

  const handleErrorMessages = (value) => {
    const key = value.key;
    const defaultValue = value.defaultValue;
    if (key === "NOT_LOCALIZED_UNEXPECTED_ERROR") {
      return defaultValue;
    } else {
      return t(key, defaultValue);
    }
  };

  const ImportStatusRenderer = (params) => {
    let statusIcon;
    if (params.value === true) {
      statusIcon = <CheckCircleIcon />;
    } else if (params.value === false) {
      statusIcon = <CrossCircleIcon />;
    }

    const statusTooltip = <Tooltip id="tooltip-right">{importStatusValueFormatter(params)}</Tooltip>;
    return (
      <>
        <OverlayTrigger key="right" placement="right" overlay={statusTooltip}>
          <span>{statusIcon}</span>
        </OverlayTrigger>
      </>
    );
  };

  const gridComponents = {
    importStatusRenderer: ImportStatusRenderer,
  };

  const columnDefs = [
    {
      headerName: t("Status", "Tila"),
      field: "success",
      valueFormatter: importStatusValueFormatter,
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: importStatusValueFormatter,
      },
      cellRenderer: "importStatusRenderer",
    },
    {
      headerName: t("Number", "Numero"),
      field: "nr",
      filter: "agNumberColumnFilter",
    },
    {
      headerName: t("Name", "Nimi"),
      field: "name",
    },
    {
      headerName: t("Action", "Toiminto"),
      field: "action",
      valueFormatter: importActionValueFormatter,
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: importActionValueFormatter,
      },
    },
    {
      headerName: t("Errors", "Virheet"),
      field: "errorMessages",
      valueGetter: importErrorValueFormatter,
      tooltipValueGetter: importErrorValueFormatter,
      filter: "agSetColumnFilter",
    },
  ];

  const getContextMenuItems = useCallback((params) => {
    return contextMenu(t, params);
  }, []);

  return (
    <Modal show onHide={hideModal} backdrop="static" size="xl" fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title>{t("ImportPersonsFromEsalary", "Tuo henkilöt ePalkoista")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-1">
          {showLoading && (
            <div className="d-flex justify-content-center">
              <Loading show={showLoading} />
            </div>
          )}

          {!showLoading && (
            <>
              <Form.Group as={Col} xl={4} controlId="firstNr">
                <Form.Label>{t("FirstPersonNumber", "Ensimmäinen henkilönumero")}</Form.Label>
                <Form.Control type="number" onChange={dataChange} value={data?.firstNr ?? ""} />
              </Form.Group>

              <Form.Group as={Col} xl={4} controlId="lastNr">
                <Form.Label>{t("LastPersonNumber", "Viimeinen henkilönumero")}</Form.Label>
                <Form.Control type="number" onChange={dataChange} value={data?.lastNr ?? ""} />
              </Form.Group>

              <Form.Group as={Col} xl={12} controlId="updateExisting">
                <Switch
                  className="mb-1"
                  id="updateExisting"
                  label={t("UpdateExisting", "Päivitä olemassa olevat")}
                  onChange={dataChange}
                  checked={data?.updateExisting ?? ""}
                />
              </Form.Group>
            </>
          )}
        </Row>

        {showResults && (
          <>
            <hr className="mb-12" />

            <Row className="mb-1">
              <Row className="mb-1">
                <Col sm="12">
                  <h3>{t("Results", "Tulokset")}</h3>
                </Col>
              </Row>

              {showResultLoading && (
                <div className="d-flex justify-content-center">
                  <Loading show={showResultLoading} />
                </div>
              )}

              {importEmployeesResult && (
                <>
                  <Row className="mb-1">
                    <Col sm="12">
                      <Alert key="info" variant="info">
                        <Row className="mb-1">
                          <Col sm="3">{t("PersonsImported", "Henkilöitä tuotiin")}:</Col>
                          <Col sm="1">
                            <strong>{importEmployeesResult.created}</strong>
                          </Col>
                          <Col sm="1"></Col>
                          <Col sm="3">{t("PersonsImportsFailed", "Henkilön tuontia epäonnistui")}:</Col>
                          <Col sm="1">
                            <strong style={{ color: "red" }}>{importEmployeesResult.createFailed}</strong>
                          </Col>
                          <Col sm="1"></Col>
                          <Col sm="1">{t("Took", "Kesto")}:</Col>
                          <Col sm="1">
                            <strong>{importEmployeesResult.took} ms</strong>
                          </Col>
                        </Row>
                        <Row className="mb-1">
                          <Col sm="3">{t("PersonsUpdated", "Henkilöitä päivitettiin")}:</Col>
                          <Col sm="1">
                            <strong>{importEmployeesResult.updated}</strong>
                          </Col>
                          <Col sm="1"></Col>
                          <Col sm="3">{t("PersonsUpdatesFailed", "Henkilön päivitystä epäonnistui")}:</Col>
                          <Col sm="1">
                            <strong style={{ color: "red" }}>{importEmployeesResult.updateFailed}</strong>
                          </Col>
                        </Row>
                      </Alert>
                    </Col>
                  </Row>

                  <Row className="styles-ag flex-grow-1 ag-theme-material" style={{ height: "700px" }}>
                    <AgGridReact
                      ref={gridRef}
                      components={gridComponents}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      sideBar={sideBar}
                      rowData={tableData}
                      getContextMenuItems={getContextMenuItems}
                      statusBar={statusBar}
                      getRowId={(params) => String(params.data.nr)}
                      getLocaleText={(params) => getLocaleText(t, params)}
                      animateRows
                      suppressCellFocus
                      enableCellTextSelection
                      onFirstDataRendered={(params) => params.api.autoSizeAllColumns()}
                      debug={import.meta.env.DEV}
                    />
                  </Row>
                </>
              )}
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={hideModal}>
          {t("Close", "Sulje")}
        </Button>
        <Button variant="primary" onClick={handleImport}>
          {t("Import", "Tuo")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
